import { initReactI18next } from 'react-i18next'

import {
  differenceInDays,
  format as formatDate,
  formatDistance,
  formatRelative,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns'
import { Locale } from 'date-fns/locale'
import i18next, { i18n, ParseKeys } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpApi from 'i18next-http-backend'
import Backend from 'i18next-http-backend'

import { LanguageCode, supportedLanguages } from '@forto-dev/platform-localisation'

import { dates, english, german, numbers } from '@/locales/index'
import { error, warn } from '@/utils/logger'

export const FALLBACK_LANGUAGE = 'en' satisfies LanguageCode

export const defaultNS = 'common'

export const resources = {
  en: {
    common: english.common,
    form: english.form,
    dates: dates,
    numbers: numbers,
  },
  de: {
    common: german.common,
    form: german.form,
    dates: dates,
    numbers: numbers,
  },
} as const

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasPath = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc?.[part], obj) !== undefined
}

export const isValidTranslationKey = (key: string | undefined): key is ParseKeys =>
  Boolean(key && hasPath(resources.en.common, key))

export const SUPPORTED_DATE_FORMATS = {
  full_date: 'full_date',
  full_date_time: 'full_date_time',
  relative: 'relative',
  relative_time: 'relative_time',
  time: 'time',
  // Deprecated formats
  long_date: 'long_date',
  long_date_timezone: 'long_date_timezone',
  short_date: 'short_date',
  short_date_time: 'short_date_time',
  medium_date: 'medium_date',
  ago: 'ago',
} as const

const DATE_FORMATS = {
  full_date: 'dd MMM yyyy',
  full_date_time: 'dd MMM yyyy, HH:mm',
  time: 'HH:mm',
  // Deprecated formats
  long_date: 'yyyy-MM-dd HH:mm',
  long_date_timezone: 'yyyy-MM-dd HH:mm (z)',
  short_date: 'dd MMM yy',
  short_date_time: 'dd MMM yy, HH:mm',
  medium_date: 'dd MMM yy',
} as const

const getRelativeDate = (
  date: Date | string,
  locale: Locale | undefined,
  lng: string = 'en-US',
  includeTime: boolean = false
) => {
  const now = new Date()
  // Normalize both dates to start of day for proper day comparison
  const startOfInput = startOfDay(date)
  const startOfNow = startOfDay(now)
  const diffInDays = differenceInDays(startOfInput, startOfNow)

  // Handle special cases (yesterday, today, tomorrow)
  if (diffInDays >= -1 && diffInDays <= 1) {
    if (includeTime) {
      // For relative_time format, use formatRelative to get the full format
      // This will always include the time as well
      return formatRelative(date, now, { locale })
    } else {
      // For relative format, use RelativeTimeFormat to get just the day reference
      const rtf = new Intl.RelativeTimeFormat(lng, { numeric: 'auto' })
      return rtf.format(diffInDays, 'day')
    }
  }

  // For dates beyond yesterday/tomorrow
  if (includeTime) {
    // Use the existing full_date_time format
    return `${formatDate(date, DATE_FORMATS.full_date, { locale })}, ${formatDate(date, DATE_FORMATS.time, { locale })}`
  }

  // Just return the full_date format for dates beyond yesterday/tomorrow
  return formatDate(date, DATE_FORMATS.full_date, { locale })
}

const getValidDate = (value: unknown): Date | null => {
  // Already a valid Date object
  if (value instanceof Date && isValid(value)) {
    return value
  }

  // Try parsing string value
  if (typeof value === 'string') {
    try {
      const parsedDate = parseISO(value)
      if (isValid(parsedDate)) {
        return parsedDate
      }
    } catch (error) {
      warn(`[i18n] Failed to parse date string: ${value}`, error)
      return null
    }
  }

  warn(`[i18n] Invalid date value passed to formatter: ${value}`)
  return null
}

export const formatDateTime = (value: string, format: string | undefined, lng: string | undefined) => {
  if (!format) {
    warn('[i18n] [formatDateTime] No format provided to formatDateTime ')
    return value
  }

  // Try to parse the date
  const date = getValidDate(value)
  if (!date) {
    return value // Return original value if invalid/unparseable
  }

  const locale = supportedLanguages.find((lang) => lang.code === lng)?.locale

  switch (format) {
    case 'full_date':
      return formatDate(date, DATE_FORMATS.full_date, { locale })

    case 'full_date_time':
      return formatDate(date, DATE_FORMATS.full_date_time, { locale })

    case 'time':
      return formatDate(date, DATE_FORMATS.time, { locale })

    case 'relative':
      return getRelativeDate(date, locale, locale?.code)

    case 'relative_time':
      return getRelativeDate(date, locale, locale?.code, true)

    // Deprecated formats
    case 'long_date':
      warn('[i18n] [formatDateTime] long_date format is deprecated. Please use full_date_time instead.')
      return formatDate(date, DATE_FORMATS.long_date, { locale })

    case 'long_date_timezone':
      warn('[i18n] [formatDateTime] long_date_timezone format is deprecated. Please use full_date_time instead.')
      return formatDate(date, DATE_FORMATS.long_date_timezone, { locale })

    case 'short_date':
      warn('[i18n] [formatDateTime] short_date format is deprecated. Please use full_date instead.')
      return formatDate(date, DATE_FORMATS.short_date, { locale })

    case 'short_date_time':
      warn('[i18n] [formatDateTime] short_date_time format is deprecated. Please use full_date_time instead.')
      return formatDate(date, DATE_FORMATS.short_date_time, { locale })

    case 'medium_date':
      warn('[i18n] [formatDateTime] medium_date format is deprecated. Please use full_date instead.')
      return formatDate(date, DATE_FORMATS.medium_date, { locale })

    case 'ago':
      warn('[i18n] [formatDateTime] ago format is deprecated. Please use relative_time instead.')
      return formatDistance(date, new Date(), { locale, addSuffix: true })

    default:
      warn(`[i18n] [formatDateTime] Unknown date time format: ${format}`)
      return value
  }
}

export const addI18nFormatters = (i18next: i18n) => {
  // add custom formatters
  i18next.services.formatter?.add('compactCount', (value) => {
    return Number(value) > 9 ? '9+' : value
  })
  i18next.services.formatter?.add('full_date', (value, lng) => {
    return formatDateTime(value, 'full_date', lng)
  })
  i18next.services.formatter?.add('full_date_time', (value, lng) => {
    return formatDateTime(value, 'full_date_time', lng)
  })
  i18next.services.formatter?.add('time', (value, lng) => {
    return formatDateTime(value, 'time', lng)
  })
  i18next.services.formatter?.add('relative', (value, lng) => {
    return formatDateTime(value, 'relative', lng)
  })
  i18next.services.formatter?.add('relative_time', (value, lng) => {
    return formatDateTime(value, 'relative_time', lng)
  })
  i18next.services.formatter?.add('long_date', (value, lng) => {
    return formatDateTime(value, 'long_date', lng)
  })
  i18next.services.formatter?.add('long_date_timezone', (value, lng) => {
    return formatDateTime(value, 'long_date_timezone', lng)
  })
  i18next.services.formatter?.add('short_date', (value, lng) => {
    return formatDateTime(value, 'short_date', lng)
  })
  i18next.services.formatter?.add('short_date_time', (value, lng) => {
    return formatDateTime(value, 'short_date_time', lng)
  })
  i18next.services.formatter?.add('medium_date', (value, lng) => {
    return formatDateTime(value, 'medium_date', lng)
  })
  i18next.services.formatter?.add('ago', (value, lng) => {
    return formatDateTime(value, 'ago', lng)
  })
}

export const initI18next = async (): Promise<i18n> =>
  new Promise((resolve) => {
    i18next
      .use(initReactI18next)
      .use(HttpApi)
      .use(
        new LanguageDetector(undefined, {
          convertDetectedLanguage: (lng) => {
            // For whatever reason we use de instead of something like de-DE
            // And en-US instead of en
            // But that's the only way the rest of the app works incl. the drop down.
            // TODO: Follow up on this and make region handling consistent
            const existing = supportedLanguages.find((supported) =>
              supported.code.split('-')[0].startsWith(lng.split('-')[0])
            )
            // Handle languages that aren't supported by defaulting to english
            return existing?.code ?? FALLBACK_LANGUAGE
          },
        })
      )
      .use(Backend)
      .init(
        {
          interpolation: {
            escapeValue: false,
          },
          // backend: {
          //   allowMultiLoading: false,
          //   crossDomain: true,
          //   loadPath: (languageCode: string[]) => {
          //     return getLocalisationURL(`/localisation/${languageCode[0]}`)
          //   },
          // },
          fallbackLng: FALLBACK_LANGUAGE,
          ns: ['common', 'dates', 'form'],
          defaultNS,
          resources,
          returnEmptyString: false,
        },
        (err) => {
          if (err) {
            error(err)
          } else {
            addI18nFormatters(i18next)
            resolve(i18next)
          }
        }
      )
  })
